import React, {useState} from 'react'
import { Link } from 'react-router-dom'
import { api } from '../../api/api'
import Swal from 'sweetalert2'
import Loader from '../Loader'

function MyVehiclesCard({vehicle}) {
  const [loading, setLoading] = useState(false);

  const handleRemoveSubscription = () => {
    Swal.fire({
      title: 'Cancel Subscription',
      text: 'Are you sure you want to cancel the subscription? Your vehicle will remain active until the end of your current billing period.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, cancel it!'
    }).then((result) => {
      if (result.isConfirmed) {
        setLoading(true);
        api.post("/users/removeSubscription", { vehicleId: vehicle._id })
          .then((response) => {
            Swal.fire('Cancelled!', response.data.message || 'Your subscription has been cancelled.', 'success')
              .then(() => window.location.reload());
          })
          .catch((error) => {
             Swal.fire('Error', error.response?.data?.message || error.toString(), 'error');
          })
          .finally(() => setLoading(false));
      }
    });
  };

  const handleRemoveVehicle = () => {
    Swal.fire({
      title: 'Remove Vehicle',
      text: 'Are you sure you want to remove this vehicle? This will also cancel any active subscription.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#6f48e5',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, remove it!',
      cancelButtonText: 'No, keep it'
    }).then((result) => {
      if (result.isConfirmed) {
        setLoading(true);
        api.post("/vehicles/removeVehicle", { vehicleId: vehicle._id })
          .then((response) => {
            setLoading(false);
            Swal.fire({
              icon: 'success',
              title: 'Vehicle Removed',
              text: 'The vehicle and its subscription have been removed successfully.'
            }).then(() => {
              window.location.reload();
            });
          })
          .catch((error) => {
            setLoading(false);
            Swal.fire({
              icon: 'error',
              title: 'Error',
              text: error.response?.data?.message || error.toString()
            });
            console.error("Error removing vehicle:", error);
          });
      }
    });
  };

  const handleResubscribe = () => {
    Swal.fire({
      title: 'Subscribe Again',
      text: `Would you like to reactivate your ${vehicle?.subscriptionPlan || 'Monthly'} subscription for this vehicle?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#10B981',
      cancelButtonColor: '#6B7280',
      confirmButtonText: 'Yes, subscribe!',
      cancelButtonText: 'Not now'
    }).then((result) => {
      if (result.isConfirmed) {
        setLoading(true);
        api.post("/users/resubscribe", { vehicleId: vehicle._id })
          .then((response) => {
            const payhereData = response.data.payhereData;
            
            Swal.fire({
              icon: "info",
              title: "Redirecting to Payment",
              text: "You will be redirected to PayHere to complete your subscription.",
              timer: 2000,
              showConfirmButton: false
            }).then(() => {
              const form = document.createElement("form");
              form.method = "POST";
              form.action = "https://www.payhere.lk/pay/checkout";
      
              for (const key in payhereData) {
                if (payhereData.hasOwnProperty(key)) {
                  const hiddenField = document.createElement("input");
                  hiddenField.type = "hidden";
                  hiddenField.name = key;
                  hiddenField.value = payhereData[key];
                  form.appendChild(hiddenField);
                }
              }
              
              Object.assign(form.style, { display: "none" });
              document.body.appendChild(form);
              form.submit();
            });
          })
          .catch((error) => {
            setLoading(false);
            Swal.fire('Error', error.response?.data?.message || error.toString(), 'error');
          });
      }
    });
  };

  // Determine subscription display state
  const isGracePeriod = vehicle?.subscriptionCancelledAt && vehicle?.isSubscribed;
  const gracePeriodEndDate = vehicle?.nextPaymentDate 
    ? new Date(vehicle.nextPaymentDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    : null;
  const canResubscribe = !vehicle?.isSubscribed || vehicle?.subscriptionStatus === 'CANCELLED' || vehicle?.subscriptionStatus === 'FAILED';

  return (
    <>
     {loading && <div className="absolute inset-0 bg-white z-10" > <Loader/> </div>}
    <div className="max-w-sm bg-white border border-gray-200 rounded-lg shadow-sm md:hover:scale-105 transition-all duration-300 w-full">
    <a href="#">
      <img
        className="rounded-t-lg aspect-custom-4-3 object-cover w-full"
        src={vehicle?.imageURLs[0]}
        alt={vehicle?.name}
      />
    </a>
    <div className="flex flex-col gap-2 p-5 w-full ">
      <p className="mb-2 text-xl lg:text-2xl font-bold tracking-tight text-gray-900">
        {vehicle?.name}
      </p>

      <div className="flex justify-between md:flex-col lg:flex-row ">
        <div className="flex flex-row space-x-2">
          <p className="font-medium">Type:</p>
          <p className="mb-2 font-normal text-gray-500">{vehicle?.type}</p>
        </div>
        <div className="flex flex-row space-x-2">
              {vehicle?.isAccepted === "accepted" ? (
                <span className="bg-green-100 text-green-800 text-sm font-medium me-2 px-2.5 py-0.5 rounded-lg">
                  Accepted
                </span>
              ) : vehicle?.isAccepted === "rejected" ? (
                <span className="bg-red-100 text-red-800 text-sm font-medium me-2 px-2.5 py-0.5 rounded-lg">
                  Rejected
                </span>
              ) : (
                <span className="bg-yellow-100 text-yellow-800 text-sm font-medium me-2 px-2.5 py-0.5 rounded-lg">
                  Pending
                </span>
              )}
            </div>
      </div>
      <div className="flex justify-between md:flex-col lg:flex-row mt-2">
        <div className="flex flex-row space-x-2 flex-wrap gap-1">
          {isGracePeriod ? (
            <span className="text-sm font-medium px-2.5 py-0.5 rounded-lg bg-orange-100 text-orange-800">
              Active until {gracePeriodEndDate || 'billing period ends'}
            </span>
          ) : (
            <span className={`text-sm font-medium px-2.5 py-0.5 rounded-lg ${vehicle?.isSubscribed ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              {vehicle?.isSubscribed ? 'Subscribed' : 'Not Subscribed'}
            </span>
          )}
          {vehicle?.subscriptionPlan && (
            <span className="text-sm font-medium px-2.5 py-0.5 rounded-lg bg-blue-100 text-blue-800">
              {vehicle?.subscriptionPlan}
            </span>
          )}
        </div>
      </div>
    

      <div className="flex flex-col items-center w-full gap-2 mt-2">
        <Link className=" w-full" to={`/view/${vehicle._id}`} >
        <button
          className="inline-flex items-center px-3 py-2 text-sm font-medium text-purple-700 bg-purple-200 rounded-lg hover:bg-purple-100 focus:outline-none transition-all duration-700  justify-center w-full"
        >
          View Details
        </button>
        </Link>
        <button className="inline-flex items-center px-3 py-2 text-sm font-medium text-red-700 bg-red-200 rounded-lg hover:bg-red-100 focus:outline-none transition-all duration-700 w-full justify-center" onClick={handleRemoveVehicle}>
          Remove Vehicle
        </button>
        {vehicle?.isSubscribed && !isGracePeriod && (
           <button className="inline-flex items-center px-3 py-2 text-sm font-medium text-orange-700 bg-orange-200 rounded-lg hover:bg-orange-100 focus:outline-none transition-all duration-700 w-full justify-center" onClick={handleRemoveSubscription}>
             Cancel Subscription
           </button>
        )}
        {isGracePeriod && (
          <button className="inline-flex items-center px-3 py-2 text-sm font-medium text-orange-700 bg-orange-200 rounded-lg hover:bg-orange-100 focus:outline-none transition-all duration-700 w-full justify-center cursor-not-allowed opacity-60" disabled>
            Cancelling on {gracePeriodEndDate || '...'}
          </button>
        )}
        {canResubscribe && !vehicle?.isSubscribed && (
          <button className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-emerald-500 rounded-lg hover:bg-emerald-600 focus:outline-none transition-all duration-700 w-full justify-center shadow-md" onClick={handleResubscribe}>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Subscribe Again
          </button>
        )}
      </div>
    </div>
  </div>
  </>
  )
}

export default MyVehiclesCard